import React, { useState } from 'react';
import { 
    Card, 
    CardContent, 
    Typography, 
    Box, 
    Chip,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Tabs,
    Tab,
    IconButton
} from '@mui/material';
import {
    TrendingUp,
    Email,
    CheckCircle,
    Flag,
    OpenInNew,
    Person
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useRegressionProspectsList } from '../hooks/useRegressionProspects';

import { supabase } from '../providers/supabase/supabase';

// Helper function for formatting numbers
const formatNumber = (num?: number): string => {
    if (!num) return '0';
    if (num >= 1000000) return `${(num/1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num/1000).toFixed(1)}K`;
    return num.toString();
};

// Helper function for priority color
const getPriorityColor = (score: number): 'error' | 'warning' | 'info' => {
    if (score >= 10) return 'error';
    if (score >= 8.5) return 'warning';
    return 'info';
};

// Helper function for status color
const getStatusColor = (status: string): 'default' | 'primary' | 'success' | 'warning' => {
    switch (status) {
        case 'pending': return 'warning';
        case 'contacted': return 'primary';
        case 'completed': return 'success';
        default: return 'default';
    }
};

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
    return (
        <div role="tabpanel" hidden={value !== index}>
            {value === index && <Box>{children}</Box>}
        </div>
    );
};

export const RegressionProspectsList: React.FC = () => {
    const [tabValue, setTabValue] = useState(0);
    const navigate = useNavigate();
    
    // Map tab index to status
    const statusMap = ['pending', 'contacted', 'completed'];
    const currentStatus = statusMap[tabValue];
    
    const { prospects, loading, error } = useRegressionProspectsList(currentStatus);

       // ADD THESE DEBUG LINES HERE:
    console.log('🔍 DEBUG - prospects:', prospects);
    console.log('🔍 DEBUG - loading:', loading);
    console.log('🔍 DEBUG - error:', error);
    console.log('🔍 DEBUG - currentStatus:', currentStatus);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleViewArtist = async (artistId: string) => {
        console.log('🔍 handleViewArtist called with artistId:', artistId);
        try {
            console.log('🔍 Querying contacts table...');
            const { data, error } = await supabase
                .from('contacts')
                .select('id')
                .eq('artist_id', artistId)
                .single();
                
            console.log('🔍 Query result - data:', data, 'error:', error);
                
            if (error || !data) {
                console.error('Contact not found for artist:', artistId);
                return;
            }
            
            console.log('🔍 Navigating to:', `/contacts/${data.id}/show`);
            navigate(`/contacts/${data.id}/show`);
        } catch (err) {
            console.error('Error finding contact:', err);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ m: 2 }}>
                Error loading regression prospects: {error}
            </Alert>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Clean header - keep TrendingUp icon, remove emojis */}
            <Box display="flex" alignItems="center" mb={3}>
                <TrendingUp color="warning" sx={{ mr: 2, fontSize: 30 }} />
                <Typography variant="h4" fontWeight="bold">
                    Regression Prospects - Next Big Thing Candidates
                </Typography>
            </Box>

            <Typography variant="body1" color="textSecondary" mb={3}>
                AI-predicted artists with high probability of breakthrough success based on cross-platform momentum analysis
            </Typography>

            {/* Status Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                    <Tab 
                        icon={<Email />} 
                        label={`Pending (${prospects.filter(p => p.contacted_status === 'pending').length})`}
                        iconPosition="start"
                    />
                    <Tab 
                        icon={<CheckCircle />} 
                        label={`Contacted (${prospects.filter(p => p.contacted_status === 'contacted').length})`}
                        iconPosition="start"
                    />
                    <Tab 
                        icon={<Flag />} 
                        label={`Completed (${prospects.filter(p => p.contacted_status === 'completed').length})`}
                        iconPosition="start"
                    />
                </Tabs>
            </Box>

            {/* Prospects Table */}
            <Card>
                <CardContent>
                    {prospects.length === 0 ? (
                        <Box textAlign="center" py={4}>
                            <Typography variant="h6" color="textSecondary">
                                No prospects found for "{currentStatus}" status
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer component={Paper} elevation={0}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Artist</strong></TableCell>
                                        <TableCell><strong>Priority</strong></TableCell>
                                        <TableCell><strong>Current Stats</strong></TableCell>
                                        <TableCell><strong>Similar To</strong></TableCell>
                                        <TableCell><strong>Momentum</strong></TableCell>
                                        <TableCell><strong>Email</strong></TableCell>
                                        <TableCell><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {prospects.map((prospect) => (
                                        <TableRow key={prospect.id} hover>
                                            <TableCell>
                                                <Box display="flex" alignItems="center">
                                                    <Person sx={{ mr: 1, color: 'text.secondary' }} />
                                                    <Box>
                                                        <Typography variant="body1" fontWeight="bold">
                                                            {prospect.name}
                                                        </Typography>
                                                        <Chip 
                                                            label={prospect.contacted_status.toUpperCase()}
                                                            color={getStatusColor(prospect.contacted_status)}
                                                            size="small"
                                                        />
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            
                                            <TableCell>
                                                <Chip 
                                                    label={prospect.priority_score.toFixed(1)}
                                                    color={getPriorityColor(prospect.priority_score)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            
                                            <TableCell>
                                                <Typography variant="caption" display="block">
                                                    Spotify: {formatNumber(prospect.spotify_monthly_listeners)}
                                                </Typography>
                                                <Typography variant="caption" display="block">
                                                    Followers: {formatNumber(prospect.spotify_followers)}
                                                </Typography>
                                                {prospect.spotify_growth_rate > 0 && (
                                                    <Typography variant="caption" color="success.main" display="block">
                                                        Growth: +{(prospect.spotify_growth_rate * 100).toFixed(1)}%
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {prospect.reference_artist_name}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    ({formatNumber(prospect.reference_artist_listeners)} listeners)
                                                </Typography>
                                            </TableCell>
                                            
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {prospect.momentum_score.toFixed(1)}/5
                                                </Typography>
                                            </TableCell>
                                            
                                            <TableCell>
                                                <Typography variant="body2" fontFamily="monospace">
                                                    {prospect.email}
                                                </Typography>
                                            </TableCell>
                                            
                                            <TableCell>
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => handleViewArtist(prospect.artist_id)}
                                                    title="View Artist Details"
                                                >
                                                    <OpenInNew />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};